import { useCallback, useRef, useState } from 'react';
import { usePlayheadStore } from '../store/playheadStore';
import { useEditorStore } from '../store/editorStore';
import { useLoopStore } from '../store/loopStore';
import { useEffectClipDialogStore } from '../store/effectClipDialogStore';
import { pixelToMs } from '../lib/timeUtils';
import { RULER_HEIGHT, TRACK_HEIGHT, TRACK_GAP, hitTestTrimHandle, hitTestFadeHandle, getTrackY, getTrackHeight } from '../lib/canvasDrawing';

type DragMode = 'none' | 'ruler' | 'trim-left' | 'trim-right' | 'move' | 'fade-in' | 'fade-out' | 'selection';

const SNAP_THRESHOLD_PX = 8;

export function useTimelineInteraction(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  const dragMode = useRef<DragMode>('none');
  const dragClipId = useRef<string | null>(null);
  const dragClipType = useRef<'clip' | 'effectClip'>('clip');
  const dragOffsetMs = useRef(0);
  const lastDragMs = useRef(0);
  const selectionStartMs = useRef(0);
  const [cursor, setCursor] = useState('pointer');

  const getTimeFromX = useCallback((clientX: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;

    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;
    return Math.max(0, pixelToMs(x, pxPerMs, scrollOffsetMs));
  }, [canvasRef]);

  const getLocalXY = useCallback((e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, [canvasRef]);

  const findSnapMs = useCallback((ms: number, clipId: string): number => {
    const { pxPerMs } = useEditorStore.getState().timeline;
    const snapThresholdMs = SNAP_THRESHOLD_PX / pxPerMs;
    const targets: number[] = [];

    // Playhead
    targets.push(usePlayheadStore.getState().currentTimeMs);

    // Other clip edges
    const tracks = useEditorStore.getState().project.tracks;
    for (const track of tracks) {
      for (const clip of track.clips) {
        if (clip.id === clipId) continue;
        targets.push(clip.startMs);
        targets.push(clip.startMs + clip.durationMs);
      }
    }

    for (const target of targets) {
      if (Math.abs(ms - target) <= snapThresholdMs) {
        return target;
      }
    }
    return ms;
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getLocalXY(e);

    // Priority 1: Click on ruler → playhead scrub or selection drag
    if (y < RULER_HEIGHT) {
      const timeMs = getTimeFromX(e.clientX);

      if (e.shiftKey) {
        // Shift+click on ruler → start selection
        dragMode.current = 'selection';
        selectionStartMs.current = timeMs;
        useLoopStore.getState().setSelection(timeMs, timeMs);
        setCursor('col-resize');
        return;
      }

      dragMode.current = 'ruler';
      usePlayheadStore.getState().setCurrentTime(timeMs);
      setCursor('col-resize');
      return;
    }

    const tracks = useEditorStore.getState().project.tracks;
    const { pxPerMs, scrollOffsetMs, selectedClipId } = useEditorStore.getState().timeline;

    // Priority 2: Fade handles (check before trim handles)
    const fadeSide = hitTestFadeHandle(x, y, tracks, selectedClipId, pxPerMs, scrollOffsetMs);
    if (fadeSide) {
      dragMode.current = fadeSide;
      dragClipId.current = selectedClipId;
      lastDragMs.current = getTimeFromX(e.clientX);
      setCursor('ew-resize');
      return;
    }

    // Priority 3: Trim handle of selected clip
    const trimSide = hitTestTrimHandle(x, y, tracks, selectedClipId, pxPerMs, scrollOffsetMs);
    if (trimSide) {
      dragMode.current = trimSide === 'left' ? 'trim-left' : 'trim-right';
      dragClipId.current = selectedClipId;
      lastDragMs.current = getTimeFromX(e.clientX);
      setCursor('col-resize');
      return;
    }

    // Priority 4: Click on clip body → select + prepare move
    // Find track by Y position using variable heights
    let trackIndex = -1;
    for (let i = 0; i < tracks.length; i++) {
      const ty = getTrackY(tracks, i);
      const th = getTrackHeight(tracks[i]);
      if (y >= ty && y < ty + th) { trackIndex = i; break; }
    }
    const track = tracks[trackIndex];
    if (!track) {
      useEditorStore.getState().selectClip(null);
      return;
    }

    const clickMs = pixelToMs(x, pxPerMs, scrollOffsetMs);

    // Check effect clips on effect tracks
    if (track.type === 'effect' && track.effectClips) {
      const effectClip = track.effectClips.find(
        (ec) => clickMs >= ec.startMs && clickMs <= ec.startMs + ec.durationMs,
      );
      if (effectClip) {
        useEditorStore.getState().selectClip(effectClip.id);
        dragMode.current = 'move';
        dragClipType.current = 'effectClip';
        dragClipId.current = effectClip.id;
        dragOffsetMs.current = clickMs - effectClip.startMs;
        lastDragMs.current = clickMs;
        setCursor('grabbing');
        return;
      }
    }

    const clip = track.clips.find(
      (c) => clickMs >= c.startMs && clickMs <= c.startMs + c.durationMs,
    );

    if (clip) {
      useEditorStore.getState().selectClip(clip.id);
      dragMode.current = 'move';
      dragClipType.current = 'clip';
      dragClipId.current = clip.id;
      dragOffsetMs.current = clickMs - clip.startMs;
      lastDragMs.current = clickMs;
      setCursor('grabbing');
    } else {
      useEditorStore.getState().selectClip(null);
      dragMode.current = 'selection';
      selectionStartMs.current = clickMs;
      useLoopStore.getState().setSelection(clickMs, clickMs);
      setCursor('text');
    }
  }, [canvasRef, getTimeFromX, getLocalXY]);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const mode = dragMode.current;

    if (mode === 'none') {
      // Update cursor on hover
      const { x, y } = getLocalXY(e);
      if (y < RULER_HEIGHT) {
        setCursor('col-resize');
        return;
      }

      const tracks = useEditorStore.getState().project.tracks;
      const { pxPerMs, scrollOffsetMs, selectedClipId } = useEditorStore.getState().timeline;

      const fadeSide = hitTestFadeHandle(x, y, tracks, selectedClipId, pxPerMs, scrollOffsetMs);
      if (fadeSide) {
        setCursor('ew-resize');
        return;
      }

      const trimSide = hitTestTrimHandle(x, y, tracks, selectedClipId, pxPerMs, scrollOffsetMs);
      if (trimSide) {
        setCursor('col-resize');
        return;
      }

      let trackIndex = -1;
      for (let i = 0; i < tracks.length; i++) {
        const ty = getTrackY(tracks, i);
        const th = getTrackHeight(tracks[i]);
        if (y >= ty && y < ty + th) { trackIndex = i; break; }
      }
      const track = tracks[trackIndex];
      if (track) {
        const clickMs = pixelToMs(x, pxPerMs, scrollOffsetMs);
        const clip = track.clips.find(
          (c) => clickMs >= c.startMs && clickMs <= c.startMs + c.durationMs,
        );
        const effectClip = track.effectClips?.find(
          (ec) => clickMs >= ec.startMs && clickMs <= ec.startMs + ec.durationMs,
        );
        setCursor(clip || effectClip ? 'grab' : 'pointer');
      } else {
        setCursor('pointer');
      }
      return;
    }

    if (mode === 'ruler') {
      const timeMs = getTimeFromX(e.clientX);
      usePlayheadStore.getState().setCurrentTime(timeMs);
      return;
    }

    if (mode === 'selection') {
      if (usePlayheadStore.getState().playing) return;
      const timeMs = getTimeFromX(e.clientX);
      useLoopStore.getState().setSelection(selectionStartMs.current, timeMs);
      return;
    }

    if (mode === 'trim-left' || mode === 'trim-right') {
      const currentMs = getTimeFromX(e.clientX);
      const deltaMs = currentMs - lastDragMs.current;
      lastDragMs.current = currentMs;
      if (dragClipId.current && deltaMs !== 0) {
        const side = mode === 'trim-left' ? 'left' : 'right';
        if (dragClipType.current === 'effectClip') {
          useEditorStore.getState().trimEffectClip(dragClipId.current, side, deltaMs);
        } else {
          useEditorStore.getState().trimClip(dragClipId.current, side, deltaMs);
        }
      }
      return;
    }

    if (mode === 'fade-in' || mode === 'fade-out') {
      const currentMs = getTimeFromX(e.clientX);
      if (!dragClipId.current) return;

      // Find the clip to compute fade duration
      const tracks = useEditorStore.getState().project.tracks;
      for (const track of tracks) {
        const clip = track.clips.find((c) => c.id === dragClipId.current);
        if (!clip) continue;

        if (mode === 'fade-in') {
          const fadeDuration = Math.max(0, Math.min(currentMs - clip.startMs, clip.durationMs * 0.9));
          useEditorStore.getState().setClipFadeIn(clip.id, fadeDuration);
        } else {
          const fadeDuration = Math.max(0, Math.min(clip.startMs + clip.durationMs - currentMs, clip.durationMs * 0.9));
          useEditorStore.getState().setClipFadeOut(clip.id, fadeDuration);
        }
        break;
      }
      return;
    }

    if (mode === 'move') {
      const currentMs = getTimeFromX(e.clientX);
      let newStartMs = currentMs - dragOffsetMs.current;
      newStartMs = findSnapMs(newStartMs, dragClipId.current!);
      if (dragClipId.current) {
        if (dragClipType.current === 'effectClip') {
          useEditorStore.getState().moveEffectClip(dragClipId.current, newStartMs);
        } else {
          useEditorStore.getState().moveClip(dragClipId.current, newStartMs);
        }
      }
      return;
    }
  }, [canvasRef, getTimeFromX, getLocalXY, findSnapMs]);

  const onMouseUp = useCallback(() => {
    if (dragMode.current === 'selection') {
      if (usePlayheadStore.getState().playing) {
        // During playback: use cached mouseDown time (auto-scroll compensation)
        const clickTime = Math.max(0, selectionStartMs.current);
        useLoopStore.getState().clearSelection();
        usePlayheadStore.getState().setCurrentTime(clickTime);
      } else {
        const { selectionStart, selectionEnd } = useLoopStore.getState();
        if (selectionStart !== null && selectionEnd !== null &&
            Math.abs(selectionEnd - selectionStart) < 100) {
          useLoopStore.getState().clearSelection();
          usePlayheadStore.getState().setCurrentTime(selectionStart);
        }
      }
    }
    dragMode.current = 'none';
    dragClipId.current = null;
    setCursor('pointer');
  }, []);

  const onContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const { x, y } = getLocalXY(e);
    if (y < RULER_HEIGHT) return null;

    const tracks = useEditorStore.getState().project.tracks;
    const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;
    let trackIndex = -1;
    for (let i = 0; i < tracks.length; i++) {
      const ty = getTrackY(tracks, i);
      const th = getTrackHeight(tracks[i]);
      if (y >= ty && y < ty + th) { trackIndex = i; break; }
    }
    const track = tracks[trackIndex];
    if (!track) return null;

    const clickMs = pixelToMs(x, pxPerMs, scrollOffsetMs);

    // Check effect clips
    if (track.type === 'effect' && track.effectClips) {
      const effectClip = track.effectClips.find(
        (ec) => clickMs >= ec.startMs && clickMs <= ec.startMs + ec.durationMs,
      );
      if (effectClip) {
        useEditorStore.getState().selectClip(effectClip.id);
        return { clipId: effectClip.id, x: e.clientX, y: e.clientY, isEffectClip: true };
      }
    }

    const clip = track.clips.find(
      (c) => clickMs >= c.startMs && clickMs <= c.startMs + c.durationMs,
    );

    if (clip) {
      useEditorStore.getState().selectClip(clip.id);
      return { clipId: clip.id, x: e.clientX, y: e.clientY };
    }

    // Right-click on empty area — check if there's an active selection for adding effects
    const loop = useLoopStore.getState();
    if (loop.selectionStart !== null && loop.selectionEnd !== null &&
        Math.abs(loop.selectionEnd - loop.selectionStart) > 50) {
      return { x: e.clientX, y: e.clientY, hasSelection: true, clipId: '' };
    }

    return null;
  }, [canvasRef, getLocalXY]);

  const onDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getLocalXY(e);
    if (y < RULER_HEIGHT) return;

    const tracks = useEditorStore.getState().project.tracks;
    const { pxPerMs, scrollOffsetMs } = useEditorStore.getState().timeline;

    // Find which track was clicked using variable heights
    let trackIndex = -1;
    for (let i = 0; i < tracks.length; i++) {
      const ty = getTrackY(tracks, i);
      const th = getTrackHeight(tracks[i]);
      if (y >= ty && y < ty + th) { trackIndex = i; break; }
    }
    const track = tracks[trackIndex];
    if (!track || track.type !== 'effect' || !track.effectClips) return;

    const clickMs = pixelToMs(x, pxPerMs, scrollOffsetMs);
    const effectClip = track.effectClips.find(
      (ec) => clickMs >= ec.startMs && clickMs <= ec.startMs + ec.durationMs,
    );

    if (effectClip) {
      useEffectClipDialogStore.getState().openForEdit(
        effectClip.id, effectClip.effectType, { ...effectClip.params },
      );
    }
  }, [canvasRef, getLocalXY]);

  return { onMouseDown, onMouseMove, onMouseUp, onContextMenu, onDoubleClick, cursor };
}
